


import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Client } from "@stomp/stompjs";
import { useLoader } from "../../../../context/LoaderProvider.jsx";

// Move FullScreenControlPanel outside of FoodVideosStats and memoize it
const FullScreenControlPanel = React.memo(({ screen, onClose, screenDetails, realTimeRemaining }) => {
    // Use a ref to store the initial data to prevent re-renders
    const initialDataRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const contentRef = useRef(null);

    const detail = screenDetails[screen?.name];

    // Store initial data when component mounts
    if (!initialDataRef.current && detail) {
        initialDataRef.current = {
            screenName: screen.name,
            deviceId: screen.deviceId,
            location: detail.location,
            currentAd: detail.currentAd ? {
                title: detail.currentAd.title,
                advertiser: detail.currentAd.advertiser,
                duration: detail.currentAd.duration,
                format: detail.currentAd.format,
                startedAt: detail.currentAd.startedAt,
                endTime: detail.currentAd.endTime,
                imageUrl: detail.currentAd.imageUrl,
                priority: detail.currentAd.priority,
                campaignCode: detail.currentAd.campaignCode,
                loopsCompleted: detail.currentAd.loopsCompleted,
                totalLoops: detail.currentAd.totalLoops,
                status: detail.currentAd.status,
                videoDuration: detail.currentAd.videoDuration,
                remainingSeconds: detail.currentAd.remainingSeconds
            } : null,
            allCampaigns: detail.allCampaigns
        };
    }

    // Helper function to check if URL is video
    const isVideoUrl = (url) => {
        if (!url) return false;
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
        const extension = url.split('.').pop()?.toLowerCase().split('?')[0];
        return videoExtensions.includes(extension) || url.includes('video') || url.includes('mp4');
    };

    // Save scroll position when scrolling
    const handleScroll = useCallback((e) => {
        if (contentRef.current) {
            scrollPositionRef.current = contentRef.current.scrollTop;
        }
    }, []);

    // Restore scroll position after render
    useEffect(() => {
        if (contentRef.current && scrollPositionRef.current > 0) {
            contentRef.current.scrollTop = scrollPositionRef.current;
        }
    });

    // Calculate remaining seconds - only update this value, not the whole component
    const remainingSeconds = useMemo(() => {
        if (!detail?.currentAd) return 0;
        const key = `${screen?.deviceId}-${detail.currentAd.campaignCode}`;
        return realTimeRemaining[key] !== undefined ? realTimeRemaining[key] : detail.currentAd.remainingSeconds;
    }, [realTimeRemaining, screen?.deviceId, detail?.currentAd?.campaignCode]);

    const remainingPercentage = useMemo(() => {
        if (!detail?.currentAd || !detail.currentAd.videoDuration) return 0;
        return Math.round((remainingSeconds / detail.currentAd.videoDuration) * 100);
    }, [remainingSeconds, detail?.currentAd?.videoDuration]);

    const formatCountdown = (seconds) => {
        if (seconds === undefined || seconds === null) return "0";
        return seconds.toString();
    };

    if (!detail || !initialDataRef.current) return null;

    const data = initialDataRef.current;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            marginTop: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backdropFilter: 'blur(5px)'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '24px',
                width: '95%',
                maxWidth: '1400px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
                {/* Header - Fixed */}
                <div style={{
                    padding: '20px 30px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    flexShrink: 0,
                    borderRadius: '24px 24px 0 0'
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 'clamp(18px, 24px, 28px)' }}>Control Panel - {screen.name}</h2>
                        <p style={{ margin: '8px 0 0', fontSize: 'clamp(14px, 16px, 18px)', opacity: 0.9 }}>
                            Device: {screen.deviceId} • Location: {data.location}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            color: 'white',
                            fontSize: 'clamp(20px, 28px, 32px)',
                            width: 'clamp(40px, 50px, 60px)',
                            height: 'clamp(40px, 50px, 60px)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            flexShrink: 0
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div
                    ref={contentRef}
                    onScroll={handleScroll}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 'clamp(15px, 30px, 40px)',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    {/* Current Playing */}
                    {data.currentAd && (
                        <div style={{
                            background: '#f0f9ff',
                            borderRadius: 'clamp(12px, 20px, 24px)',
                            padding: 'clamp(20px, 30px, 40px)',
                            marginBottom: '30px'
                        }}>
                            <h3 style={{ margin: '0 0 20px', color: '#0284c7', fontSize: 'clamp(16px, 20px, 24px)' }}>🔴 NOW PLAYING</h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: 'clamp(15px, 30px, 40px)'
                            }}>
                                {/* Media Preview */}
                                <div style={{
                                    background: '#e2e8f0',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '250px',
                                    aspectRatio: '16/9'
                                }}>
                                    {data.currentAd.imageUrl ? (
                                        isVideoUrl(data.currentAd.imageUrl) ? (
                                            <video
                                                src={data.currentAd.imageUrl}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                controls
                                                autoPlay
                                                muted
                                                loop
                                            />
                                        ) : (
                                            <img
                                                src={data.currentAd.imageUrl}
                                                alt={data.currentAd.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        )
                                    ) : (
                                        <span style={{ fontSize: 'clamp(40px, 64px, 80px)' }}>
                                            {data.currentAd.format === 'video' ? '🎬' : '🖼️'}
                                        </span>
                                    )}
                                </div>

                                {/* Details */}
                                <div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: 'clamp(10px, 20px, 25px)'
                                    }}>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Campaign</div>
                                            <div style={{ fontSize: 'clamp(16px, 18px, 20px)', fontWeight: '600' }}>{data.currentAd.title}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Code</div>
                                            <div style={{ fontSize: 'clamp(16px, 18px, 20px)', fontWeight: '600' }}>{data.currentAd.campaignCode}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Format</div>
                                            <div>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    fontSize: 'clamp(12px, 14px, 16px)',
                                                    background: data.currentAd.format === 'video' ? '#2196F320' : '#FF980020',
                                                    color: data.currentAd.format === 'video' ? '#2196F3' : '#FF9800'
                                                }}>
                                                    {data.currentAd.format}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Priority</div>
                                            <div>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    fontSize: 'clamp(12px, 14px, 16px)',
                                                    background: data.currentAd.priority === 'HIGH' ? '#ef444420' :
                                                        data.currentAd.priority === 'MEDIUM' ? '#f59e0b20' : '#10b98120',
                                                    color: data.currentAd.priority === 'HIGH' ? '#ef4444' :
                                                        data.currentAd.priority === 'MEDIUM' ? '#f59e0b' : '#10b981'
                                                }}>
                                                    {data.currentAd.priority}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Started At</div>
                                            <div style={{ fontSize: 'clamp(14px, 16px, 18px)' }}>{data.currentAd.startedAt}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Ends At</div>
                                            <div style={{ fontSize: 'clamp(14px, 16px, 18px)' }}>{data.currentAd.endTime}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Duration</div>
                                            <div style={{ fontSize: 'clamp(14px, 16px, 18px)' }}>{data.currentAd.duration}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Loops</div>
                                            <div style={{ fontSize: 'clamp(14px, 16px, 18px)' }}>{data.currentAd.loopsCompleted || 0} / {data.currentAd.totalLoops || 0}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>Status</div>
                                            <div>
                                                <span style={{
                                                    color: data.currentAd.status === 'ACTIVE' ? '#10b981' : '#94a3b8',
                                                    fontSize: 'clamp(14px, 16px, 18px)'
                                                }}>
                                                    {data.currentAd.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 'clamp(12px, 14px, 16px)', color: '#64748b' }}>⏱️ Remaining</div>
                                            <div style={{ fontSize: 'clamp(24px, 32px, 40px)', fontWeight: '700', color: '#10b981' }}>
                                                {formatCountdown(remainingSeconds)}s
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'clamp(12px, 14px, 16px)', marginBottom: '8px' }}>
                                            <span>Progress</span>
                                            <span style={{ color: '#10b981' }}>
                                                {100 - remainingPercentage}% completed
                                            </span>
                                        </div>
                                        <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${100 - remainingPercentage}%`,
                                                background: 'linear-gradient(90deg, #10b981, #059669)',
                                                borderRadius: '5px',
                                                transition: 'width 1s linear'
                                            }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* All Campaigns Table */}
                    <h3 style={{ margin: '0 0 20px', fontSize: 'clamp(16px, 20px, 24px)' }}>📋 All Campaigns</h3>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Campaign</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Code</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Format</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Priority</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Duration</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Loops</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Completed</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Start Date</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>End Date</th>
                                    <th style={{ padding: 'clamp(10px, 15px, 20px)', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Remaining</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.allCampaigns?.map((campaign, index) => {
                                    const isPlaying = campaign.screens[0]?.status === 'playing';
                                    const key = `${screen.deviceId}-${campaign.id}`;
                                    const remainingSecs = isPlaying ? (realTimeRemaining[key] || campaign.videoDuration) : 0;

                                    return (
                                        <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)', fontWeight: '500' }}>{campaign.title}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.campaignCode}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: 'clamp(11px, 12px, 13px)',
                                                    background: campaign.adFormat === 'video' ? '#2196F320' : '#FF980020',
                                                    color: campaign.adFormat === 'video' ? '#2196F3' : '#FF9800'
                                                }}>
                                                    {campaign.adFormat}
                                                </span>
                                            </td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: 'clamp(11px, 12px, 13px)',
                                                    background: campaign.status === 'ACTIVE' ? '#10b98120' : '#94a3b820',
                                                    color: campaign.status === 'ACTIVE' ? '#10b981' : '#64748b'
                                                }}>
                                                    {campaign.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: 'clamp(11px, 12px, 13px)',
                                                    background: campaign.priority === 'HIGH' ? '#ef444420' :
                                                        campaign.priority === 'MEDIUM' ? '#f59e0b20' : '#10b98120',
                                                    color: campaign.priority === 'HIGH' ? '#ef4444' :
                                                        campaign.priority === 'MEDIUM' ? '#f59e0b' : '#10b981'
                                                }}>
                                                    {campaign.priority}
                                                </span>
                                            </td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.duration}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.loops || 0}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.loopsCompleted || 0}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.startDate}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>{campaign.endDate}</td>
                                            <td style={{ padding: 'clamp(10px, 15px, 20px)' }}>
                                                {isPlaying ? (
                                                    <span style={{ color: '#10b981', fontWeight: '600', fontSize: 'clamp(14px, 16px, 18px)' }}>
                                                        {formatCountdown(remainingSecs)}s
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#94a3b8' }}>-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    // Only re-render if screen changes or realTimeRemaining changes for the current playing ad
    if (prevProps.screen?.deviceId !== nextProps.screen?.deviceId) {
        return false; // Re-render if screen changed
    }

    if (prevProps.onClose !== nextProps.onClose) {
        return false; // Re-render if onClose changed
    }

    // Check if the currently playing ad's remaining time changed
    const prevScreenDetail = prevProps.screenDetails[prevProps.screen?.name];
    const nextScreenDetail = nextProps.screenDetails[nextProps.screen?.name];

    const prevCurrentAd = prevScreenDetail?.currentAd;
    const nextCurrentAd = nextScreenDetail?.currentAd;

    if (prevCurrentAd && nextCurrentAd) {
        const prevKey = `${prevProps.screen?.deviceId}-${prevCurrentAd.campaignCode}`;
        const nextKey = `${nextProps.screen?.deviceId}-${nextCurrentAd.campaignCode}`;

        if (prevProps.realTimeRemaining[prevKey] !== nextProps.realTimeRemaining[nextKey]) {
            return false; // Re-render if remaining time changed
        }
    }

    return true; // Skip re-render
});

const FoodVideosStats = () => {
    const [screensData, setScreensData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedView, setSelectedView] = useState("grid");
    const [selectedScreen, setSelectedScreen] = useState("all");
    const [liveTime, setLiveTime] = useState(new Date());
    const [error, setError] = useState(null);
    const [wsConnected, setWsConnected] = useState(false);
    const [wsMessages, setWsMessages] = useState([]);
    const [connectedDevices, setConnectedDevices] = useState([]);
    const [selectedDeviceForControl, setSelectedDeviceForControl] = useState(null);
    const [showControlPanel, setShowControlPanel] = useState(false);
    const [realTimeRemaining, setRealTimeRemaining] = useState({});

    const { showLoader, hideLoader } = useLoader();
    const stompClientRef = useRef(null);
    const mountedRef = useRef(true);
    const subscriptionsRef = useRef(new Map());
    const timerRef = useRef(null);

    // Real-time countdown timer - but only update when panel is not open to prevent re-renders
    useEffect(() => {
        // Update remaining seconds every second
        timerRef.current = setInterval(() => {
            if (!mountedRef.current) return;

            // Only update realTimeRemaining if control panel is not open to prevent re-renders
            if (!showControlPanel) {
                setRealTimeRemaining(prev => {
                    const newRemaining = {};
                    let hasChanges = false;

                    // Update all playing ads
                    Object.keys(prev).forEach(key => {
                        if (prev[key] > 0) {
                            newRemaining[key] = prev[key] - 1;
                            hasChanges = true;
                        } else {
                            newRemaining[key] = 0;
                        }
                    });

                    return hasChanges ? newRemaining : prev;
                });
            }
        }, 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [showControlPanel]);

    // Initialize real-time remaining when campaigns change
    useEffect(() => {
        const newRemaining = {};

        screensData.forEach(screen => {
            if (screen.campaigns) {
                screen.campaigns.forEach(campaign => {
                    const now = new Date();
                    const startTime = new Date(campaign.startDate);
                    const endTime = new Date(campaign.endDate);
                    const isPlaying = campaign.active && now >= startTime && now <= endTime;

                    if (isPlaying && campaign.durationSeconds) {
                        const key = `${screen.deviceId}-${campaign.campaignId}`;
                        newRemaining[key] = campaign.durationSeconds;
                    }
                });
            }
        });

        setRealTimeRemaining(newRemaining);
    }, [screensData]);

    // WebSocket topics configuration
    const wsTopics = [
        {
            name: 'screen-updates',
            path: (deviceId) => `/topic/digital-screen-updates/${deviceId}`,
            description: 'Screen campaign updates'
        }
    ];

    // Helper function to get thumbnail emoji based on type
    const getThumbnailForType = (type) => {
        switch (type?.toUpperCase()) {
            case 'IMAGE': return '🖼️';
            case 'VIDEO': return '🎬';
            default: return '📺';
        }
    };

    // Helper function to check if URL is video
    const isVideoUrl = (url) => {
        if (!url) return false;
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
        const extension = url.split('.').pop()?.toLowerCase().split('?')[0];
        return videoExtensions.includes(extension) || url.includes('video') || url.includes('mp4');
    };

    // Comprehensive token retrieval function
    const getAuthToken = () => {
        // Common token key names to check
        const possibleKeys = [
            'token',
            'accessToken',
            'authToken',
            'jwt',
            'userToken',
            'access_token',
            'auth_token',
            'jwt_token',
            'Authorization',
            'user',
            'auth',
            'loginToken',
            'sessionToken'
        ];

        // Try to get token from various locations
        let token = null;

        const tokenSources = [
            { source: 'localStorage', key: 'token' },
            { source: 'localStorage', key: 'accessToken' },
            { source: 'localStorage', key: 'authToken' },
            { source: 'localStorage', key: 'jwt' },
            { source: 'localStorage', key: 'userToken' },
            { source: 'sessionStorage', key: 'token' },
            { source: 'sessionStorage', key: 'accessToken' },
            { source: 'sessionStorage', key: 'authToken' },
            { source: 'sessionStorage', key: 'jwt' }
        ];

        for (const { source, key } of tokenSources) {
            const storage = source === 'localStorage' ? localStorage : sessionStorage;
            const value = storage.getItem(key);
            if (value) {
                // Try to parse if it's JSON
                if (value.startsWith('{') || value.startsWith('[')) {
                    try {
                        const parsed = JSON.parse(value);
                        if (parsed.token) {
                            token = parsed.token;
                            break;
                        }
                        if (parsed.accessToken) {
                            token = parsed.accessToken;
                            break;
                        }
                        if (parsed.jwt) {
                            token = parsed.jwt;
                            break;
                        }
                        if (parsed.data && parsed.data.token) {
                            token = parsed.data.token;
                            break;
                        }
                    } catch (e) {
                        token = value;
                        break;
                    }
                } else {
                    token = value;
                    break;
                }
            }
        }

        return token;
    };

    // WebSocket connection function - now connects to multiple devices
    const connectWebSocket = () => {
        // Don't reconnect if already connected
        if (stompClientRef.current?.connected) {
            console.log(`WebSocket already connected`);
            setWsConnected(true);
            return;
        }

        console.log(`🟢 Connecting WebSocket...`);

        // Create new STOMP client
        const client = new Client({
            brokerURL: "ws://testing.maamaas.com:8080/promotions/ws",
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: (str) => {
                if (process.env.NODE_ENV !== 'production') {
                    console.log("STOMP:", str);
                }
            }
        });

        // Handle successful connection
        client.onConnect = () => {
            if (!mountedRef.current) return;

            console.log("✅ WebSocket Connected Successfully");
            setWsConnected(true);

            // Subscribe to topics for all screens that have campaigns
            const devicesToConnect = new Set();

            screensData.forEach(screen => {
                if (screen.deviceId && screen.campaigns && screen.campaigns.length > 0) {
                    devicesToConnect.add(screen.deviceId);
                }
            });

            // If no screens with campaigns, still connect to first screen
            if (devicesToConnect.size === 0 && screensData.length > 0) {
                devicesToConnect.add(screensData[0].deviceId);
            }

            console.log(`📡 Connecting to ${devicesToConnect.size} devices:`, Array.from(devicesToConnect));

            devicesToConnect.forEach(deviceId => {
                wsTopics.forEach(topicConfig => {
                    const topicPath = topicConfig.path(deviceId);

                    try {
                        const subscription = client.subscribe(topicPath, (message) => {
                            try {
                                // Try to parse as JSON
                                let data;
                                try {
                                    data = JSON.parse(message.body);
                                } catch {
                                    data = message.body;
                                }

                                console.log(`📦 Message received on ${topicConfig.name} for device ${deviceId}:`, data);

                                // Store message
                                const messageWithTime = {
                                    topic: topicConfig.name,
                                    deviceId: deviceId,
                                    data: data,
                                    timestamp: new Date().toISOString()
                                };

                                setWsMessages(prev => [messageWithTime, ...prev].slice(0, 50));

                                // Handle different message types
                                handleWebSocketMessage(topicConfig.name, deviceId, data);

                            } catch (error) {
                                console.error(`❌ Error handling message for ${topicPath}:`, error);
                            }
                        });

                        // Store subscription
                        subscriptionsRef.current.set(`${topicPath}-${deviceId}`, subscription);
                        console.log(`  ✅ Subscribed to: ${topicPath}`);

                    } catch (error) {
                        console.error(`❌ Error subscribing to ${topicPath}:`, error);
                    }
                });
            });

            setConnectedDevices(Array.from(devicesToConnect));

            // Store connection info
            localStorage.setItem('ws_connected', 'true');
        };

        // Handle STOMP errors
        client.onStompError = (frame) => {
            console.error("❌ STOMP Error:", frame.headers["message"]);
            console.error("Details:", frame.body);
            if (mountedRef.current) {
                setWsConnected(false);
            }
        };

        // Handle WebSocket close
        client.onWebSocketClose = (event) => {
            console.log("⚠️ WebSocket Closed:", event);
            if (mountedRef.current) {
                setWsConnected(false);
            }
            localStorage.setItem('ws_connected', 'false');
        };

        // Handle WebSocket errors
        client.onWebSocketError = (event) => {
            console.error("🔴 WebSocket Error:", event);
            if (mountedRef.current) {
                setWsConnected(false);
            }
        };

        // Store client reference
        stompClientRef.current = client;

        // Activate connection
        client.activate();
    };

    // Disconnect WebSocket
    const disconnectWebSocket = () => {
        console.log("🔌 Disconnecting WebSocket");

        // Unsubscribe from all topics
        subscriptionsRef.current.forEach((subscription, topicPath) => {
            try {
                subscription.unsubscribe();
                console.log(`  ✅ Unsubscribed from ${topicPath}`);
            } catch (error) {
                console.error(`  ❌ Error unsubscribing from ${topicPath}:`, error);
            }
        });

        // Clear subscriptions
        subscriptionsRef.current.clear();

        // Deactivate client
        if (stompClientRef.current) {
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
        }

        // Reset state
        setWsConnected(false);
        setConnectedDevices([]);
        localStorage.removeItem('ws_connected');
        localStorage.removeItem('ws_device');
    };

    // Handle different WebSocket message types
    const handleWebSocketMessage = (topic, deviceId, data) => {
        if (!mountedRef.current) return;

        switch (topic) {
            case 'screen-updates':
                // Update screen data when campaign updates received
                if (data && data.campaigns) {
                    setScreensData(prevData =>
                        prevData.map(screen =>
                            screen.deviceId === deviceId
                                ? { ...screen, campaigns: data.campaigns }
                                : screen
                        )
                    );
                }
                break;

            default:
                console.log('Unknown topic:', topic);
        }
    };

    // Reconnect WebSocket when screens data changes
    useEffect(() => {
        if (screensData.length > 0) {
            // Disconnect existing connection
            disconnectWebSocket();
            // Connect with new device list
            setTimeout(() => {
                if (mountedRef.current) {
                    connectWebSocket();
                }
            }, 500);
        }
    }, [screensData.length]);

    // Fetch screens data from API
    const fetchScreensData = async () => {
        setLoading(true);
        setError(null);
        showLoader();

        try {
            const token = getAuthToken();

            if (!token) {
                throw new Error('No authentication token found. Please ensure you are logged in.');
            }

            const response = await fetch(
                `https://backend.maamaas.com/promotions/api/user/screens-with-data`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (response.status === 401) {
                throw new Error('Session expired. Please login again.');
            }

            if (response.status === 403) {
                throw new Error('Access denied. You don\'t have permission to view screens.');
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log("Screens data:", data);
            setScreensData(data);

        } catch (err) {
            console.error("❌ Error fetching screens data:", err);
            setError(err.message || "Failed to fetch screens data");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    // Clear all WebSocket messages
    const clearWsMessages = () => {
        setWsMessages([]);
    };

    // Handle box click - set selected screen for dropdown
    const handleBoxClick = (screen) => {
        setSelectedScreen(screen.name);
    };

    // Handle control button click - open full screen control panel
    const handleControlClick = useCallback((screen, e) => {
        e.stopPropagation(); // Prevent the box click from also firing
        setSelectedDeviceForControl(screen);
        setShowControlPanel(true);
        // Prevent body scrolling when control panel is open
        document.body.style.overflow = 'hidden';
    }, []);

    // Close control panel
    const handleCloseControlPanel = useCallback(() => {
        setShowControlPanel(false);
        // Don't clear selectedDeviceForControl immediately to prevent flash
        setTimeout(() => {
            setSelectedDeviceForControl(null);
        }, 300);
        // Restore body scrolling
        document.body.style.overflow = 'auto';
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        // Fetch initial screens data
        fetchScreensData();

        // Set up live time update
        const timer = setInterval(() => {
            if (mountedRef.current) {
                setLiveTime(new Date());
            }
        }, 1000);

        // Cleanup function
        return () => {
            mountedRef.current = false;
            clearInterval(timer);
            // Restore body scrolling on unmount
            document.body.style.overflow = 'auto';

            // Disconnect WebSocket when component unmounts
            disconnectWebSocket();
        };
    }, []);

    // Transform all screens data
    const allScreens = useMemo(() => {
        if (!screensData.length) return [];

        return screensData.map((screen, index) => ({
            id: index + 1,
            name: screen.screenName || "Unnamed Screen",
            location: screen.location || "Unknown Location",
            type: "Digital Display",
            size: "Unknown",
            deviceId: screen.deviceId,
            totalCampaigns: screen.totalCampaigns || 0,
            campaigns: screen.campaigns || []
        }));
    }, [screensData]);

    // Filter screens based on selection
    const filteredScreens = useMemo(() => {
        if (selectedScreen === "all") return allScreens;
        return allScreens.filter(screen => screen.name === selectedScreen);
    }, [allScreens, selectedScreen]);

    // Transform API campaigns to video format for all screens
    const campaignsAsVideos = useMemo(() => {
        if (!screensData.length) return {};

        const videosByScreen = {};

        screensData.forEach(screen => {
            const screenName = screen.screenName || "Unnamed Screen";
            videosByScreen[screenName] = [];

            if (screen.campaigns && screen.campaigns.length > 0) {
                screen.campaigns.forEach((campaign, index) => {
                    const now = new Date();
                    const startTime = new Date(campaign.startDate);
                    const endTime = new Date(campaign.endDate);

                    const mockStartTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                    const mockEndTime = new Date(now.getTime() + campaign.durationSeconds * 1000)
                        .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

                    const isPlaying = campaign.active && now >= startTime && now <= endTime;

                    videosByScreen[screenName].push({
                        id: campaign.campaignId || index + 1,
                        title: campaign.campaignName,
                        adFormat: campaign.advertisementType?.toLowerCase() || "image",
                        description: campaign.description || "",
                        screens: [
                            {
                                name: screenName,
                                location: screen.location || "Unknown",
                                status: isPlaying ? "playing" : "queued",
                                startTime: mockStartTime,
                                endTime: mockEndTime,
                                duration: `${campaign.durationSeconds} sec`,
                                startedAt: isPlaying ? mockStartTime : null,
                                endsIn: isPlaying ? `${Math.floor(campaign.durationSeconds / 60)}:${(campaign.durationSeconds % 60).toString().padStart(2, '0')}` : null,
                                remainingSeconds: isPlaying ? campaign.durationSeconds : 0,
                                remainingPercentage: isPlaying ? 50 : 0,
                                queuePosition: !isPlaying ? index + 1 : undefined,
                                startsIn: !isPlaying ? `${Math.floor((new Date(campaign.startDate) - now) / 60000)}:00` : undefined
                            }
                        ],
                        duration: `${campaign.durationSeconds} sec`,
                        videoDuration: campaign.durationSeconds,
                        thumbnail: getThumbnailForType(campaign.advertisementType),
                        category: campaign.goal || "General",
                        advertiser: `Customer ${campaign.customerId}`,
                        startDate: new Date(campaign.startDate).toLocaleDateString(),
                        endDate: new Date(campaign.endDate).toLocaleDateString(),
                        timePeriod: campaign.period || "PER_DAY",
                        views: campaign.totalLoops || 0,
                        revenue: 0,
                        imageUrl: campaign.imageUrl,
                        priority: campaign.priority,
                        loops: campaign.loops,
                        status: campaign.status,
                        campaignCode: campaign.campaignCode,
                        loopsCompleted: campaign.loopsCompleted || 0,
                        totalLoops: campaign.totalLoops || 0,
                        deviceId: screen.deviceId,
                        screenName: screenName
                    });
                });
            }
        });

        return videosByScreen;
    }, [screensData]);

    // Stats calculation based on actual data
    const stats = useMemo(() => {
        const now = new Date();
        const currentTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

        if (!screensData.length) {
            return {
                currentTime,
                totalPlaying: 0,
                totalQueued: 0,
                totalIdle: allScreens.length,
                totalScreens: allScreens.length,
                totalRevenue: 0,
                totalViews: 0,
                totalAdContent: "0h 0m",
                totalRemaining: "0m 0s",
                avgAdsPerScreen: "0",
                adsPerScreen: {},
                mostActiveScreen: ["None", 0],
                wsConnected,
                connectedDevices: connectedDevices.length
            };
        }

        let totalPlaying = 0;
        let totalQueued = 0;
        let totalRemainingSeconds = 0;
        let totalAdSeconds = 0;
        let totalViews = 0;
        const adsPerScreen = {};

        screensData.forEach(screen => {
            const screenName = screen.screenName || "Unnamed Screen";
            adsPerScreen[screenName] = screen.campaigns?.length || 0;

            if (screen.campaigns) {
                screen.campaigns.forEach(campaign => {
                    const now = new Date();
                    const startTime = new Date(campaign.startDate);
                    const endTime = new Date(campaign.endDate);
                    const isPlaying = campaign.active && now >= startTime && now <= endTime;

                    if (isPlaying) {
                        totalPlaying++;
                        const key = `${screen.deviceId}-${campaign.campaignId}`;
                        totalRemainingSeconds += realTimeRemaining[key] || campaign.durationSeconds || 0;
                    } else if (campaign.active) {
                        totalQueued++;
                    }
                    totalAdSeconds += campaign.durationSeconds || 0;
                    totalViews += campaign.totalLoops || 0;
                });
            }
        });

        const totalHours = Math.floor(totalAdSeconds / 3600);
        const totalMinutes = Math.floor((totalAdSeconds % 3600) / 60);
        const remainingMinutes = Math.floor(totalRemainingSeconds / 60);
        const remainingSeconds = totalRemainingSeconds % 60;

        // Find most active screen
        let mostActiveScreen = ["None", 0];
        Object.entries(adsPerScreen).forEach(([name, count]) => {
            if (count > mostActiveScreen[1]) {
                mostActiveScreen = [name, count];
            }
        });

        const totalIdle = allScreens.filter(screen =>
            !screensData.find(s => s.screenName === screen.name)?.campaigns?.length
        ).length;

        return {
            currentTime,
            totalPlaying,
            totalQueued,
            totalIdle,
            totalScreens: allScreens.length,
            totalRevenue: 0,
            totalViews,
            totalAdContent: `${totalHours}h ${totalMinutes}m`,
            totalRemaining: `${remainingMinutes}m ${remainingSeconds}s`,
            avgAdsPerScreen: (totalPlaying + totalQueued > 0 ? ((totalPlaying + totalQueued) / allScreens.length).toFixed(1) : "0"),
            adsPerScreen,
            mostActiveScreen,
            wsConnected,
            connectedDevices: connectedDevices.length
        };
    }, [screensData, allScreens, wsConnected, connectedDevices, realTimeRemaining]);

    // Screen details for all screens - memoized to prevent unnecessary recalculations
    const screenDetails = useMemo(() => {
        if (!screensData.length) return {};

        const details = {};

        screensData.forEach(screen => {
            const screenName = screen.screenName || "Unnamed Screen";
            const screenCampaigns = campaignsAsVideos[screenName] || [];

            const playingAds = screenCampaigns.filter(video =>
                video.screens.some(s => s.name === screenName && s.status === "playing")
            );

            const queuedAds = screenCampaigns.filter(video =>
                video.screens.some(s => s.name === screenName && s.status === "queued")
            );

            const currentPlaying = playingAds.length > 0 ? playingAds[0] : null;
            const currentScreenDetail = currentPlaying?.screens.find(s => s.name === screenName);

            // Get real-time remaining seconds for current playing ad
            let remainingSeconds = currentScreenDetail?.remainingSeconds || 0;
            if (currentPlaying) {
                const key = `${screen.deviceId}-${currentPlaying.id}`;
                remainingSeconds = realTimeRemaining[key] !== undefined ? realTimeRemaining[key] : remainingSeconds;
            }

            details[screenName] = {
                name: screenName,
                location: screen.location || "Unknown",
                type: "Digital Display",
                size: "Unknown",
                status: playingAds.length > 0 ? "playing" : queuedAds.length > 0 ? "queued" : "idle",
                currentAd: currentPlaying ? {
                    title: currentPlaying.title,
                    advertiser: currentPlaying.advertiser,
                    duration: currentPlaying.duration,
                    format: currentPlaying.adFormat,
                    startedAt: currentScreenDetail?.startedAt,
                    endsIn: currentScreenDetail?.endsIn,
                    remainingSeconds: remainingSeconds,
                    remainingPercentage: currentPlaying.videoDuration > 0
                        ? Math.round((remainingSeconds / currentPlaying.videoDuration) * 100)
                        : 0,
                    endTime: currentScreenDetail?.endTime,
                    imageUrl: currentPlaying.imageUrl,
                    priority: currentPlaying.priority,
                    campaignCode: currentPlaying.campaignCode,
                    loopsCompleted: currentPlaying.loopsCompleted || 0,
                    totalLoops: currentPlaying.totalLoops || 0,
                    status: currentPlaying.status,
                    videoDuration: currentPlaying.videoDuration
                } : null,
                queue: queuedAds.map((ad, index) => {
                    const screenInfo = ad.screens.find(s => s.name === screenName);
                    return {
                        title: ad.title,
                        advertiser: ad.advertiser,
                        duration: ad.duration,
                        format: ad.adFormat,
                        startTime: screenInfo?.startTime,
                        startsIn: screenInfo?.startsIn,
                        queuePosition: screenInfo?.queuePosition || index + 1,
                        imageUrl: ad.imageUrl,
                        campaignCode: ad.campaignCode,
                        loops: ad.loops,
                        status: ad.status
                    };
                }).sort((a, b) => a.queuePosition - b.queuePosition),
                totalAdsToday: screenCampaigns.length,
                totalDurationToday: screenCampaigns.reduce((sum, ad) => {
                    const screenInfo = ad.screens.find(s => s.name === screenName);
                    return sum + (screenInfo?.remainingSeconds || 0);
                }, 0),
                deviceId: screen.deviceId,
                allCampaigns: screenCampaigns
            };
        });

        return details;
    }, [screensData, campaignsAsVideos, realTimeRemaining]);

    const formatRemainingTime = (seconds) => {
        if (seconds === undefined || seconds === null) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatCountdown = (seconds) => {
        if (seconds === undefined || seconds === null) return "0";
        return seconds.toString();
    };

    // Styles definition (keep all your existing styles)
    const styles = {
        container: {
            padding: '30px',
            // background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            height: '70vh',
            overflow: 'auto',
            scrollbarWidth: 'none',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '24px',
            margin: '20px'
        },
        loadingContent: {
            textAlign: 'center',
            background: 'rgba(255,255,255,0.95)',
            padding: '40px',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        },
        loadingSpinner: {
            width: '50px',
            height: '50px',
            border: '3px solid #f1f5f9',
            borderTopColor: '#b15cd6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
        },
        loadingText: {
            color: '#1e293b',
            fontSize: '16px',
            fontWeight: '500',
            margin: 0
        },
        errorContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '24px',
            margin: '20px'
        },
        errorContent: {
            textAlign: 'center',
            background: 'white',
            padding: '40px',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            maxWidth: '400px'
        },
        errorIcon: {
            fontSize: '48px',
            marginBottom: '20px',
            display: 'block'
        },
        errorTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#ef4444',
            marginBottom: '10px'
        },
        errorMessage: {
            fontSize: '14px',
            color: '#64748b',
            marginBottom: '20px'
        },
        retryButton: {
            padding: '10px 24px',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            ':hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }
        },
        header: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
        },
        headerContent: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
        },
        liveIndicator: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 16px',
            borderRadius: '40px',
            width: 'fit-content',
            marginBottom: '15px',
            backdropFilter: 'blur(10px)'
        },
        liveDot: {
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#ef4444',
            animation: 'pulse 1.5s infinite'
        },
        liveText: {
            color: 'white',
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '1px'
        },
        liveTime: {
            color: 'rgba(255,255,255,0.8)',
            fontSize: '13px',
            fontWeight: '500'
        },
        title: {
            fontSize: '32px',
            fontWeight: '700',
            color: 'white',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px'
        },
        subtitle: {
            fontSize: '16px',
            color: 'rgba(255,255,255,0.9)',
            margin: 0
        },
        viewSelector: {
            display: 'flex',
            gap: '10px',
            background: 'rgba(255,255,255,0.15)',
            padding: '5px',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
        },
        viewButton: {
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            background: 'transparent',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
        },
        viewButtonActive: {
            background: 'white',
            color: '#667eea',
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '30px'
        },
        statCard: {
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.05)',
            transition: 'transform 0.3s ease',
            cursor: 'pointer',
            ':hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }
        },
        statIcon: (color) => ({
            width: '60px',
            height: '60px',
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${color}20, ${color}05)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            border: `1px solid ${color}30`
        }),
        statInfo: {
            flex: 1
        },
        statValue: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#1e293b',
            display: 'block',
            marginBottom: '4px',
            lineHeight: 1.2
        },
        statLabel: {
            fontSize: '14px',
            color: '#64748b',
            display: 'block',
            marginBottom: '4px',
            fontWeight: '500'
        },
        statDetail: {
            fontSize: '12px',
            color: '#94a3b8'
        },
        filterSection: {
            marginBottom: '20px'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 15px 0'
        },
        filterBar: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
        },
        screenSelect: {
            padding: '12px 20px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            background: 'white',
            color: '#1e293b',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '300px',
            cursor: 'pointer',
            outline: 'none',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        },
        legend: {
            display: 'flex',
            gap: '20px'
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#64748b'
        },
        legendDotPlaying: {
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#10b981',
            boxShadow: '0 0 0 2px #10b98120'
        },
        legendDotQueued: {
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#f59e0b',
            boxShadow: '0 0 0 2px #f59e0b20'
        },
        legendDotIdle: {
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#94a3b8',
            boxShadow: '0 0 0 2px #94a3b820'
        },
        screenGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
        },
        screenCard: {
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            ':hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            }
        },
        screenCardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '15px'
        },
        screenCardTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 4px 0'
        },
        screenCardLocation: {
            fontSize: '13px',
            color: '#64748b',
            margin: 0
        },
        screenCardDeviceId: {
            fontSize: '11px',
            color: '#94a3b8',
            margin: '2px 0 0 0'
        },
        screenCardStatus: {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            color: 'white'
        },
        screenSpecs: {
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            padding: '10px',
            background: '#f8fafc',
            borderRadius: '10px'
        },
        specItem: {
            fontSize: '12px',
            color: '#475569',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        nowPlaying: {
            background: '#f0f9ff',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '15px'
        },
        playingHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
        },
        playingLabel: {
            fontSize: '11px',
            fontWeight: '600',
            color: '#0284c7',
            letterSpacing: '0.5px'
        },
        playingFormat: (format) => ({
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: '600',
            background: format === 'video' ? '#2196F3' :
                format === 'image' ? '#FF9800' :
                    format === 'text' ? '#9C27B0' : '#E91E63',
            color: 'white'
        }),
        playingTitle: {
            fontSize: '15px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 4px 0'
        },
        playingAdvertiser: {
            fontSize: '12px',
            color: '#64748b',
            margin: '0 0 12px 0'
        },
        timeDetails: {
            background: 'white',
            borderRadius: '8px',
            padding: '10px',
            marginBottom: '12px'
        },
        timeRow: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#475569',
            padding: '4px 0'
        },
        timeRowHighlight: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#475569',
            padding: '4px 0',
            background: '#fef2f2',
            borderRadius: '4px',
            marginTop: '4px',
            padding: '6px'
        },
        timeValue: {
            fontWeight: '500',
            color: '#1e293b'
        },
        remainingTime: {
            fontWeight: '700',
            color: '#10b981',
            fontSize: '16px',
            animation: 'pulse 2s infinite'
        },
        progressSection: {
            marginTop: '10px'
        },
        progressHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: '#64748b',
            marginBottom: '4px'
        },
        timeRemaining: {
            color: '#10b981',
            fontWeight: '600'
        },
        progressBar: {
            height: '6px',
            background: '#e2e8f0',
            borderRadius: '3px',
            overflow: 'hidden'
        },
        progressFill: (percent) => ({
            height: '100%',
            width: `${percent}%`,
            background: 'linear-gradient(90deg, #10b981, #059669)',
            borderRadius: '3px',
            transition: 'width 0.3s ease'
        }),
        miniProgressBar: {
            height: '4px',
            background: '#e2e8f0',
            borderRadius: '2px',
            overflow: 'hidden',
            marginTop: '8px'
        },
        miniProgressFill: (percent) => ({
            height: '100%',
            width: `${percent}%`,
            background: 'linear-gradient(90deg, #10b981, #059669)',
            borderRadius: '2px',
            transition: 'width 0.3s ease'
        }),
        queueSection: {
            marginTop: '15px'
        },
        queueHeader: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#f59e0b',
            marginBottom: '10px'
        },
        queueItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            background: '#fef3c7',
            borderRadius: '8px',
            marginBottom: '8px'
        },
        queueItemLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        queuePosition: {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: '#f59e0b',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        queueTitle: {
            fontSize: '13px',
            fontWeight: '500',
            color: '#1e293b',
            display: 'block'
        },
        queueAdvertiser: {
            fontSize: '11px',
            color: '#64748b'
        },
        queueTime: {
            textAlign: 'right'
        },
        startsIn: {
            display: 'block',
            fontSize: '10px',
            color: '#f59e0b',
            fontWeight: '500'
        },
        idleContent: {
            textAlign: 'center',
            padding: '20px'
        },
        idleIcon: {
            fontSize: '40px',
            marginBottom: '10px',
            opacity: 0.5
        },
        idleMessage: {
            fontSize: '13px',
            color: '#94a3b8',
            marginBottom: '5px'
        },
        idleSubMessage: {
            fontSize: '11px',
            color: '#94a3b8',
            margin: '4px 0 0 0'
        },
        scheduleButton: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            ':hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }
        },
        listView: {
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            marginBottom: '30px',
            overflowX: 'auto'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse'
        },
        th: {
            textAlign: 'left',
            padding: '12px',
            borderBottom: '2px solid #e2e8f0',
            color: '#475569',
            fontSize: '13px',
            fontWeight: '600'
        },
        tr: {
            borderBottom: '1px solid #e2e8f0',
            cursor: 'pointer',
            ':hover': {
                background: '#f8fafc'
            }
        },
        td: {
            padding: '12px',
            fontSize: '13px',
            color: '#1e293b'
        },
        tdSmall: {
            fontSize: '11px',
            color: '#94a3b8',
            marginTop: '2px'
        },
        statusBadge: {
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '500',
            color: 'white'
        },
        remainingBadge: {
            background: '#10b98120',
            color: '#10b981',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600'
        },
        queueCount: {
            background: '#fef3c7',
            color: '#f59e0b',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '500'
        },
        detailedView: {
            marginTop: '40px'
        },
        scheduleGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '20px',
            marginTop: '20px'
        },
        scheduleCard: {
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.05)'
        },
        scheduleHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
        },
        scheduleNumber: {
            fontSize: '12px',
            fontWeight: '600',
            color: '#64748b'
        },
        scheduleFormat: (format) => ({
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: '600',
            background: format === 'video' ? '#2196F3' :
                format === 'image' ? '#FF9800' :
                    format === 'text' ? '#9C27B0' : '#E91E63',
            color: 'white'
        }),
        scheduleTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 4px 0'
        },
        scheduleAdvertiser: {
            fontSize: '13px',
            color: '#64748b',
            margin: '0 0 15px 0'
        },
        scheduleDetails: {
            background: '#f8fafc',
            borderRadius: '10px',
            padding: '12px',
            marginBottom: '15px'
        },
        detailRow: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#475569',
            padding: '4px 0'
        },
        screenTimeInfo: {
            background: '#f0f9ff',
            borderRadius: '10px',
            padding: '12px'
        },
        timeBadge: (status) => ({
            fontSize: '11px',
            fontWeight: '600',
            color: status === 'playing' ? '#10b981' :
                status === 'queued' ? '#f59e0b' : '#94a3b8',
            marginBottom: '10px'
        }),
        timeLeftHighlight: {
            color: '#10b981',
            fontWeight: '600'
        },
        adImagePreview: {
            marginBottom: '12px',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#f1f5f9',
            textAlign: 'center',
            maxHeight: '200px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        previewImage: {
            maxWidth: '100%',
            maxHeight: '180px',
            objectFit: 'contain'
        },
        previewVideo: {
            maxWidth: '100%',
            maxHeight: '180px',
            objectFit: 'contain',
            background: '#000'
        },
        scheduleImagePreview: {
            marginBottom: '12px',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#f1f5f9',
            textAlign: 'center',
            padding: '8px',
            maxHeight: '120px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        schedulePreviewImage: {
            maxWidth: '100%',
            maxHeight: '100px',
            objectFit: 'contain'
        },
        schedulePreviewVideo: {
            maxWidth: '100%',
            maxHeight: '100px',
            objectFit: 'contain',
            background: '#000'
        },
        priorityBadge: (priority) => ({
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            background: priority === 'HIGH' ? '#ef444420' :
                priority === 'MEDIUM' ? '#f59e0b20' : '#10b98120',
            color: priority === 'HIGH' ? '#ef4444' :
                priority === 'MEDIUM' ? '#f59e0b' : '#10b981'
        }),
        activeStatus: {
            color: '#10b981',
            fontWeight: '600'
        },
        inactiveStatus: {
            color: '#94a3b8',
            fontWeight: '600'
        },
        wsStatusContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '12px',
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '30px',
            width: 'fit-content',
            backdropFilter: 'blur(10px)',
            flexWrap: 'wrap'
        },
        wsDot: (connected) => ({
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: connected ? '#10b981' : '#ef4444',
            animation: connected ? 'pulse 2s infinite' : 'none',
            boxShadow: connected ? '0 0 0 3px #10b98140' : '0 0 0 3px #ef444440'
        }),
        wsText: {
            color: 'rgba(255,255,255,0.9)',
            fontSize: '12px',
            fontWeight: '500'
        },
        wsDeviceBadge: {
            padding: '4px 10px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            fontSize: '11px',
            color: 'white',
            fontWeight: '500'
        },
        wsMessagesPanel: {
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            display: 'none' // Hide the WebSocket messages panel
        },
        wsMessagesHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            flexWrap: 'wrap',
            gap: '10px'
        },
        wsMessagesTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b'
        },
        wsClearButton: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            background: '#ef4444',
            color: 'white',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': {
                background: '#dc2626'
            }
        },
        wsMessagesTable: {
            width: '100%',
            borderCollapse: 'collapse'
        },
        wsTh: {
            textAlign: 'left',
            padding: '12px',
            borderBottom: '2px solid #e2e8f0',
            color: '#475569',
            fontSize: '13px',
            fontWeight: '600',
            background: '#f8fafc'
        },
        wsTr: {
            borderBottom: '1px solid #e2e8f0',
            ':hover': {
                background: '#f8fafc'
            }
        },
        wsTd: {
            padding: '12px',
            fontSize: '13px',
            color: '#1e293b'
        },
        wsMessageTopic: {
            fontWeight: '600',
            color: '#667eea',
            background: '#667eea10',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block'
        },
        wsMessageDevice: {
            fontWeight: '600',
            color: '#f59e0b',
            background: '#f59e0b10',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block'
        },
        wsMessageTime: {
            color: '#64748b',
            fontWeight: '500'
        },
        wsMessageData: {
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#475569'
        },
        controlButton: {
            padding: '6px 12px',
            border: 'none',
            borderRadius: '8px',
            background: '#10b981',
            color: 'white',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            marginLeft: '8px',
            transition: 'all 0.2s ease',
            ':hover': {
                background: '#059669'
            },
            zIndex: 10,
            position: 'relative'
        },
        headerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        }
    };

    // Add style element for animations
    React.useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.5; }
                100% { opacity: 1; }
            }
        `;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.loadingContent}>
                    <div style={styles.loadingSpinner}></div>
                    <p style={styles.loadingText}>Loading screens dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorContainer}>
                <div style={styles.errorContent}>
                    <span style={styles.errorIcon}>⚠️</span>
                    <h2 style={styles.errorTitle}>Failed to Load Screens Data</h2>
                    <p style={styles.errorMessage}>{error}</p>
                    <button onClick={fetchScreensData} style={styles.retryButton}>
                        🔄 Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!screensData.length) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.loadingContent}>
                    <p style={styles.loadingText}>No screens data available</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Full Screen Control Panel Popup - Now properly memoized */}
            {showControlPanel && selectedDeviceForControl && (
                <FullScreenControlPanel
                    screen={selectedDeviceForControl}
                    onClose={handleCloseControlPanel}
                    screenDetails={screenDetails}
                    realTimeRemaining={realTimeRemaining}
                />
            )}

            {/* Header with Live Status */}
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    <div>
                        <div style={styles.liveIndicator}>
                            <span style={styles.liveDot}></span>
                            <span style={styles.liveText}>LIVE BROADCAST</span>
                            <span style={styles.liveTime}>{stats.currentTime}</span>
                        </div>
                        <h1 style={styles.title}>Screens Advertising Dashboard</h1>
                        <p style={styles.subtitle}>
                            {stats.totalScreens} Screens • {stats.totalPlaying} Playing • {stats.totalQueued} Queued • {stats.totalIdle} Idle
                        </p>

                        {/* WebSocket Status with connected devices */}
                        <div style={styles.wsStatusContainer}>
                            <span style={styles.wsDot(wsConnected)}></span>
                            <span style={styles.wsText}>
                                WebSocket: {wsConnected ? 'Connected' : 'Disconnected'}
                            </span>
                            {wsConnected && stats.connectedDevices > 0 && (
                                <span style={styles.wsDeviceBadge}>
                                    📡 {stats.connectedDevices} device{stats.connectedDevices !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* View Selector */}
                    <div style={styles.viewSelector}>
                        <button
                            style={{
                                ...styles.viewButton,
                                ...(selectedView === 'grid' ? styles.viewButtonActive : {})
                            }}
                            onClick={() => setSelectedView('grid')}
                        >
                            📱 Grid View
                        </button>
                        <button
                            style={{
                                ...styles.viewButton,
                                ...(selectedView === 'list' ? styles.viewButtonActive : {})
                            }}
                            onClick={() => setSelectedView('list')}
                        >
                            📋 List View
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statIcon('#667eea')}>📺</div>
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.totalScreens}</span>
                        <span style={styles.statLabel}>Total Screens</span>
                        <span style={styles.statDetail}>Most active: {stats.mostActiveScreen[0]}</span>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={styles.statIcon('#10b981')}>🎬</div>
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.totalPlaying}</span>
                        <span style={styles.statLabel}>Playing Now</span>
                        <span style={styles.statDetail}>
                            {stats.totalPlaying > 0 ? `${((stats.totalPlaying / stats.totalScreens) * 100).toFixed(0)}% active` : 'No active ads'}
                        </span>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={styles.statIcon('#f59e0b')}>⏳</div>
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.totalQueued}</span>
                        <span style={styles.statLabel}>In Queue</span>
                        <span style={styles.statDetail}>Next up in queue</span>
                    </div>
                </div>

                <div style={styles.statCard}>
                    <div style={styles.statIcon('#b15cd6')}>⏱️</div>
                    <div style={styles.statInfo}>
                        <span style={styles.statValue}>{stats.totalRemaining}</span>
                        <span style={styles.statLabel}>Total Remaining</span>
                        <span style={styles.statDetail}>across all ads</span>
                    </div>
                </div>
            </div>

            {/* Screen Filter */}
            <div style={styles.filterSection}>
                <h2 style={styles.sectionTitle}>📋 Screen Directory</h2>
                <div style={styles.filterBar}>
                    <select
                        value={selectedScreen}
                        onChange={(e) => setSelectedScreen(e.target.value)}
                        style={styles.screenSelect}
                    >
                        <option value="all">🎯 All Screens ({allScreens.length})</option>
                        {allScreens.map(screen => (
                            <option key={screen.deviceId} value={screen.name}>
                                {screen.name} - {screen.location} ({screen.totalCampaigns} ads)
                            </option>
                        ))}
                    </select>

                    <div style={styles.headerActions}>
                        <div style={styles.legend}>
                            <span style={styles.legendItem}>
                                <span style={styles.legendDotPlaying}></span> Playing
                            </span>
                            <span style={styles.legendItem}>
                                <span style={styles.legendDotQueued}></span> Queued
                            </span>
                            <span style={styles.legendItem}>
                                <span style={styles.legendDotIdle}></span> Idle
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Screen Grid/List View */}
            {selectedView === 'grid' ? (
                <div style={styles.screenGrid}>
                    {filteredScreens.map(screen => {
                        const detail = screenDetails[screen.name] || {
                            status: 'idle',
                            location: screen.location,
                            totalAdsToday: screen.totalCampaigns
                        };
                        return (
                            <div
                                key={screen.deviceId}
                                onClick={() => handleBoxClick(screen)}
                                style={{
                                    ...styles.screenCard,
                                    borderTop: `4px solid ${detail.status === 'playing' ? '#10b981' :
                                        detail.status === 'queued' ? '#f59e0b' : '#94a3b8'
                                        }`
                                }}
                            >
                                <div style={styles.screenCardHeader}>
                                    <div>
                                        <h3 style={styles.screenCardTitle}>{screen.name}</h3>
                                        <p style={styles.screenCardLocation}>{detail.location}</p>
                                        <p style={styles.screenCardDeviceId}>Device: {screen.deviceId}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{
                                            ...styles.screenCardStatus,
                                            backgroundColor:
                                                detail.status === 'playing' ? '#10b981' :
                                                    detail.status === 'queued' ? '#f59e0b' : '#94a3b8'
                                        }}>
                                            {detail.status === 'playing' && '🔴 LIVE'}
                                            {detail.status === 'queued' && '⏳ QUEUED'}
                                            {detail.status === 'idle' && '⏸️ IDLE'}
                                        </span>
                                        <button
                                            onClick={(e) => handleControlClick(screen, e)}
                                            style={styles.controlButton}
                                        >
                                            🎮 Control
                                        </button>
                                    </div>
                                </div>

                                <div style={styles.screenSpecs}>
                                    <span style={styles.specItem}>📺 Digital Display</span>
                                    <span style={styles.specItem}>📊 {detail.totalAdsToday} campaigns</span>
                                    <span style={styles.specItem}>📍 {screen.location}</span>
                                </div>

                                {detail.status === 'playing' && detail.currentAd && (
                                    <div style={styles.nowPlaying}>
                                        {detail.currentAd.imageUrl && (
                                            <div style={styles.adImagePreview}>
                                                {isVideoUrl(detail.currentAd.imageUrl) ? (
                                                    <video
                                                        src={detail.currentAd.imageUrl}
                                                        style={styles.previewVideo}
                                                        controls
                                                        muted
                                                        loop
                                                        autoPlay
                                                    />
                                                ) : (
                                                    <img
                                                        src={detail.currentAd.imageUrl}
                                                        alt={detail.currentAd.title}
                                                        style={styles.previewImage}
                                                        onError={(e) => e.target.style.display = 'none'}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        <div style={styles.playingHeader}>
                                            <span style={styles.playingLabel}>NOW PLAYING</span>
                                            <span style={styles.playingFormat(detail.currentAd.format)}>
                                                {detail.currentAd.format}
                                            </span>
                                        </div>
                                        <h4 style={styles.playingTitle}>{detail.currentAd.title}</h4>
                                        <p style={styles.playingAdvertiser}>
                                            {detail.currentAd.advertiser} • Priority: {detail.currentAd.priority}
                                        </p>

                                        <div style={styles.timeDetails}>
                                            <div style={styles.timeRow}>
                                                <span>Campaign:</span>
                                                <span style={styles.timeValue}>{detail.currentAd.campaignCode}</span>
                                            </div>
                                            <div style={styles.timeRow}>
                                                <span>Started:</span>
                                                <span style={styles.timeValue}>{detail.currentAd.startedAt}</span>
                                            </div>
                                            <div style={styles.timeRow}>
                                                <span>Duration:</span>
                                                <span style={styles.timeValue}>{detail.currentAd.duration}</span>
                                            </div>
                                            <div style={styles.timeRow}>
                                                <span>Ends at:</span>
                                                <span style={styles.timeValue}>{detail.currentAd.endTime}</span>
                                            </div>
                                            <div style={styles.timeRowHighlight}>
                                                <span>⏱️ Remaining:</span>
                                                <span style={styles.remainingTime}>
                                                    {detail.currentAd.remainingSeconds}s
                                                </span>
                                            </div>
                                        </div>

                                        <div style={styles.progressSection}>
                                            <div style={styles.progressHeader}>
                                                <span>Progress</span>
                                                <span style={styles.timeRemaining}>
                                                    {100 - detail.currentAd.remainingPercentage}% completed
                                                </span>
                                            </div>
                                            <div style={styles.progressBar}>
                                                <div style={styles.progressFill(
                                                    100 - (detail.currentAd.remainingPercentage || 0)
                                                )}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {detail.status === 'queued' && detail.queue.length > 0 && (
                                    <div style={styles.queueSection}>
                                        <div style={styles.queueHeader}>
                                            <span>⏳ QUEUE ({detail.queue.length})</span>
                                        </div>
                                        {detail.queue.map((ad, index) => (
                                            <div key={index} style={styles.queueItem}>
                                                <div style={styles.queueItemLeft}>
                                                    <span style={styles.queuePosition}>{ad.queuePosition}</span>
                                                    <div>
                                                        <span style={styles.queueTitle}>{ad.title}</span>
                                                        <span style={styles.queueAdvertiser}>
                                                            {ad.advertiser} • {ad.campaignCode}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={styles.queueTime}>
                                                    <span>⏰ {ad.startTime}</span>
                                                    {ad.startsIn && (
                                                        <span style={styles.startsIn}>in {ad.startsIn}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {detail.status === 'idle' && (
                                    <div style={styles.idleContent}>
                                        <div style={styles.idleIcon}>⏸️</div>
                                        <p style={styles.idleMessage}>No active campaigns</p>
                                        <p style={styles.idleSubMessage}>Waiting for scheduled campaigns</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={styles.listView}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Screen</th>
                                <th style={styles.th}>Location</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Current Ad</th>
                                <th style={styles.th}>Campaign Code</th>
                                <th style={styles.th}>Duration</th>
                                <th style={styles.th}>Priority</th>
                                <th style={styles.th}>Started</th>
                                <th style={styles.th}>Remaining</th>
                                <th style={styles.th}>Queue</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredScreens.map(screen => {
                                const detail = screenDetails[screen.name] || {
                                    status: 'idle',
                                    currentAd: null,
                                    queue: []
                                };
                                return (
                                    <tr
                                        key={screen.deviceId}
                                        style={styles.tr}
                                        onClick={() => handleBoxClick(screen)}
                                    >
                                        <td style={styles.td}>
                                            <strong>{screen.name}</strong>
                                            <div style={styles.tdSmall}>Digital Display</div>
                                        </td>
                                        <td style={styles.td}>{detail.location}</td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                backgroundColor:
                                                    detail.status === 'playing' ? '#10b981' :
                                                        detail.status === 'queued' ? '#f59e0b' : '#94a3b8'
                                            }}>
                                                {detail.status}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? detail.currentAd.title : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? detail.currentAd.campaignCode : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? detail.currentAd.duration : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? (
                                                <span style={styles.priorityBadge(detail.currentAd.priority)}>
                                                    {detail.currentAd.priority}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? detail.currentAd.startedAt : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.currentAd ? (
                                                <span style={styles.remainingBadge}>
                                                    {detail.currentAd.remainingSeconds}s
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            {detail.queue.length > 0 ? (
                                                <span style={styles.queueCount}>{detail.queue.length} ads</span>
                                            ) : '-'}
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleControlClick(screen, e);
                                                }}
                                                style={styles.controlButton}
                                            >
                                                Control
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Detailed Schedule View - This shows when box is clicked */}
            {selectedScreen !== "all" && campaignsAsVideos[selectedScreen]?.length > 0 && (
                <div style={styles.detailedView}>
                    <h2 style={styles.sectionTitle}>📊 {selectedScreen} - Campaign Schedule</h2>

                    <div style={styles.scheduleGrid}>
                        {campaignsAsVideos[selectedScreen].map((ad, index) => {
                            const screenInfo = ad.screens.find(s => s.name === selectedScreen);
                            const key = `${ad.deviceId}-${ad.id}`;
                            const remainingSecs = screenInfo?.status === 'playing' ? (realTimeRemaining[key] || ad.videoDuration) : 0;

                            return (
                                <div key={ad.id} style={styles.scheduleCard}>
                                    {ad.imageUrl && (
                                        <div style={styles.scheduleImagePreview}>
                                            {isVideoUrl(ad.imageUrl) ? (
                                                <video
                                                    src={ad.imageUrl}
                                                    style={styles.schedulePreviewVideo}
                                                    controls
                                                    muted
                                                    loop
                                                />
                                            ) : (
                                                <img
                                                    src={ad.imageUrl}
                                                    alt={ad.title}
                                                    style={styles.schedulePreviewImage}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div style={styles.scheduleHeader}>
                                        <span style={styles.scheduleNumber}>Campaign #{index + 1}</span>
                                        <span style={styles.scheduleFormat(ad.adFormat)}>
                                            {ad.adFormat.toUpperCase()}
                                        </span>
                                    </div>

                                    <h3 style={styles.scheduleTitle}>{ad.title}</h3>
                                    <p style={styles.scheduleAdvertiser}>
                                        {ad.campaignCode} • Priority: {ad.priority}
                                    </p>

                                    <div style={styles.scheduleDetails}>
                                        <div style={styles.detailRow}>
                                            <span>📅 Campaign Period:</span>
                                            <span>{ad.startDate} to {ad.endDate}</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span>⏱️ Duration:</span>
                                            <span>{ad.duration} ({ad.videoDuration} seconds)</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span>📊 Period:</span>
                                            <span>{ad.timePeriod}</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span>🔄 Loops:</span>
                                            <span>{ad.loops} total, {ad.views} played</span>
                                        </div>
                                        <div style={styles.detailRow}>
                                            <span>📋 Status:</span>
                                            <span style={ad.status === 'ACTIVE' ? styles.activeStatus : styles.inactiveStatus}>
                                                {ad.status}
                                            </span>
                                        </div>
                                    </div>

                                    {screenInfo && (
                                        <div style={styles.screenTimeInfo}>
                                            <div style={styles.timeBadge(screenInfo.status)}>
                                                {screenInfo.status === 'playing' ? '🔴 Playing Now' :
                                                    screenInfo.status === 'queued' ? '⏳ In Queue' : '✅ Completed'}
                                            </div>
                                            {screenInfo.status === 'playing' && (
                                                <>
                                                    <div style={styles.timeRow}>
                                                        <span>Started:</span>
                                                        <span>{screenInfo.startedAt}</span>
                                                    </div>
                                                    <div style={styles.timeRow}>
                                                        <span>Ends:</span>
                                                        <span>{screenInfo.endTime}</span>
                                                    </div>
                                                    <div style={styles.timeRowHighlight}>
                                                        <span>⏱️ Remaining:</span>
                                                        <span style={styles.remainingTimeLarge}>
                                                            {remainingSecs}s
                                                        </span>
                                                    </div>
                                                    <div style={styles.miniProgressBar}>
                                                        <div style={styles.miniProgressFill(
                                                            100 - (remainingSecs / ad.videoDuration * 100)
                                                        )}></div>
                                                    </div>
                                                </>
                                            )}
                                            {screenInfo.status === 'queued' && (
                                                <>
                                                    <div style={styles.timeRow}>
                                                        <span>Starts at:</span>
                                                        <span>{screenInfo.startTime}</span>
                                                    </div>
                                                    <div style={styles.timeRow}>
                                                        <span>Starts in:</span>
                                                        <span style={styles.timeLeftHighlight}>{screenInfo.startsIn}</span>
                                                    </div>
                                                    <div style={styles.timeRow}>
                                                        <span>Queue Position:</span>
                                                        <span>#{screenInfo.queuePosition}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FoodVideosStats;